import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { mutate } from "swr";

import useCompetition from "../../../../../hooks/useCompetition";
import useCurrentUser from "../../../../../hooks/useCurrentUser";
import { judgeCompetition, patchProfile } from "../../../../../api";
import { useJudgeAuthWall } from "../../hooks/useAuthWall";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import getGenericErrorInfo from "../../../../../utils/getGenericErrorInfo";
import useErrorAlert from "../../../../../hooks/useErrorAlert";

const schema = Yup.object().shape({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  profilePicture: Yup.mixed<File | { uuid: string; url: string }>()
    .optional()
    .test(
      "FILE_FORMAT",
      "Please select an image",
      (value: File | object | undefined) => {
        return value instanceof File ? value.type?.startsWith("image/") : true;
      }
    ),
});

export type ProfileValues = Yup.Asserts<typeof schema>;

export default function useJudgeConfirmScreen() {
  useJudgeAuthWall();

  const params = useParams();

  const { setError } = useErrorAlert();

  const [judgingKey] = useLocalStorage(
    `${params.competitionUUID}/judgingKey`,
    ""
  );

  const { profile } = useCurrentUser();
  const { competition } = useCompetition(params.competitionUUID);

  const navigate = useNavigate();

  const initialValues: ProfileValues = {
    firstName: profile?.firstName ?? "",
    lastName: profile?.lastName ?? "",
    profilePicture:
      profile?.picture && profile?.pictureUrl
        ? { uuid: profile.picture, url: profile.pictureUrl }
        : undefined,
  } as ProfileValues;

  const onSubmit = async (values: ProfileValues) => {
    if (!competition || !profile) {
      return;
    }
    try {
      const updatedProfile = await patchProfile({
        firstName: values.firstName,
        lastName: values.lastName,
        profilePicture: values.profilePicture,
      });
      mutate("/profiles/me", updatedProfile);

      await judgeCompetition({
        competitionUUID: competition.uuid,
        judgingKey,
      });

      mutate(`/competitions/${competition.uuid}`);
      mutate(`/competitions/${competition.uuid}/entries`);

      navigate(`/${competition.uuid}/judge`);
    } catch (error) {
      setError(getGenericErrorInfo(error));
    }
  };

  return {
    profile,
    competition,
    schema,
    canChangeCategory: true,
    onSubmit,
    initialValues,
  };
}
